import {
  RefObject, useCallback, useEffect, useRef, useState,
} from 'react';

import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import { getPageCount } from 'components/DesignSystem/Pagination/pageWindow';
import { MyStudiesListResult } from 'helper/flux/myStudiesAdapter';
import { serializeFilters, toQuery } from 'helper/flux/myStudiesFilters';
import { todayInSaoPaulo } from 'helper/flux/myStudiesPeriod';
import { UseMyStudiesFiltersResult } from 'utils/hooks/flux/personalized/useMyStudiesFilters';
import useMyStudiesListState, {
  MyStudiesListState,
} from 'utils/hooks/flux/personalized/useMyStudiesListState';
import useStudiesErrorState, {
  StudiesErrorKind,
} from 'utils/hooks/flux/personalized/useStudiesErrorState';
import { DateOnly, MyStudiesQuery } from 'types/Flux/Personalized/myStudies';
import { MyStudiesFilters } from 'types/Flux/Personalized/myStudiesFilters';

/** Área com a barra de rolagem do conteúdo da página (`Layout/Main/index.tsx:101`). */
export const SCROLL_CONTAINER_SELECTOR = '.new-home-outlet-container';

/** Estados com uma lista na tela: nítida, esmaecida ou sob a faixa de erro. */
const LIST_ON_SCREEN: readonly MyStudiesListState[] = ['ready', 'pending', 'error-stale'];

export interface UseMyStudiesPageOptions {
  /** Sem usuário válido, a consulta espera o login, como a URL do `useMyStudiesFilters`. */
  skip?: boolean;
  /**
   * Elemento da tela: a troca de página rola o `.new-home-outlet-container` que o contém
   * (`closest`), e não um container qualquer do documento. Sem ele, nada rola.
   */
  scrollAnchorRef?: RefObject<HTMLElement | null>;
}

export interface UseMyStudiesPageResult {
  /**
   * Última lista recebida (`data` do RTK Query). No `pending` ainda é a dos argumentos
   * anteriores, que fica esmaecida; no `error-initial`, pode ser a de outro filtro.
   */
  list: MyStudiesListResult | undefined;
  /**
   * A consulta passada à `api.useListQuery` neste render (`toQuery(filters, new Date())`).
   * Quem lê a mesma entrada do cache usa esta, nunca outra montada à parte: o polling de status
   * (#2001) recebe `listArgs` daqui.
   */
  listArgs: MyStudiesQuery;
  /** Estado visual da lista (#1992). */
  state: MyStudiesListState;
  /** Há lista na tela (`ready`, `pending` ou `error-stale`), e não skeleton nem bloco de erro. */
  hasListOnScreen: boolean;
  /**
   * A lista na tela é de uma página além da última (sem itens, com total > 0): a correção para a
   * última página está a caminho. A tela mostra o skeleton e esconde a paginação até ela chegar.
   */
  isBeyondLastPage: boolean;
  /** Tipo do erro tratado na tela (#1984), ou `null`. */
  errorKind: StudiesErrorKind | null;
  /** Fim da última resposta bem-sucedida dos argumentos atuais (`fulfilledTimeStamp`), em ms. */
  lastUpdatedAt: number | undefined;
  /** "Agora" das linhas, em ms: muda com a lista e com o dia em São Paulo, não a cada tecla. */
  nowMs: number;
  /** Refaz a consulta da página atual: "Tentar de novo" (#1999) e "Atualizar" (#2004). */
  refetchPage: () => void;
  /**
   * Um `refetchPage` pedido nos argumentos ATUAIS ainda não terminou (com sucesso ou erro). Trocar
   * de página ou de filtro durante o refetch não deixa a lista nova esmaecida.
   */
  manualRefreshPending: boolean;
}

/** O que a página usa da instância única do `useMyStudiesFilters` da tela. */
export type MyStudiesPageFilters = Pick<UseMyStudiesFiltersResult, 'filters' | 'normalizePage'>;

/**
 * "Agora" das linhas. Os textos relativos ("hoje", "ontem") só mudam com o dia de São Paulo; um
 * `Date.now()` a cada render mudaria a cada tecla da busca e derrubaria o `memo` da lista. Relido
 * quando chega outra lista ou quando vira o dia (ajuste de estado durante o render, sem efeito).
 */
function useRowsNow(list: MyStudiesListResult | undefined): number {
  const today = todayInSaoPaulo(new Date());
  const [clock, setClock] = useState<{ list: typeof list; today: DateOnly; nowMs: number }>(
    () => ({ list, today, nowMs: Date.now() }),
  );
  if (clock.list === list && clock.today === today) return clock.nowMs;

  const next = { list, today, nowMs: Date.now() };
  setClock(next);
  return next.nowMs;
}

/**
 * Leva a área de conteúdo ao topo quando a página ou o por página mudam (inclusive voltando no
 * navegador e na correção da página), mas não ao montar. Filtrar estando na página 1 não rola.
 */
function useScrollToTopOnPageChange(
  { page, perPage }: MyStudiesFilters,
  scrollAnchorRef: RefObject<HTMLElement | null> | undefined,
): void {
  const pageKey = `${page}:${perPage}`;
  const previousPageKey = useRef(pageKey);

  useEffect(() => {
    if (previousPageKey.current === pageKey) return;
    previousPageKey.current = pageKey;
    const container = scrollAnchorRef?.current?.closest<HTMLElement>(SCROLL_CONTAINER_SELECTOR);
    if (container) container.scrollTop = 0;
  }, [pageKey, scrollAnchorRef]);
}

/**
 * A lista é de uma página além da última? Usa a página e o por página da PRÓPRIA resposta: até a
 * página corrigida chegar, a URL já mudou, mas a lista na tela continua a da página vazia.
 */
function isPageBeyondEnd(list: MyStudiesListResult | undefined): boolean {
  if (list === undefined || list.total <= 0 || list.items.length > 0) return false;
  return list.pageNumber > getPageCount(list.total, list.pageSize);
}

/**
 * Página da lista da aba Meus Estudos (#1999): filtros → consulta → estado.
 *
 * - Consulta: `toQuery(filters, new Date())` a cada render, exposta em `listArgs` (o polling de
 *   status da #2001 lê a mesma entrada do cache). O RTK Query compara os argumentos
 *   serializados (chave canônica da #1993), então a mesma consulta não refaz a requisição, e cada
 *   `{página, por página, filtros}` é uma entrada de cache (60 s). A resposta de argumentos
 *   antigos é descartada (filtros interativos durante o skeleton, D15).
 * - Estado: `useMyStudiesListState` com `hasError: error !== undefined` (não o `isError` do RTK) e
 *   o refetch manual pendente. Erros: `useStudiesErrorState(error, { mode: 'primary' })`, nunca
 *   `useHandleRequestErrors`: falha de rede ou de servidor fica na tela, sem ir para /error.
 * - Correção: com a resposta dos argumentos atuais e nada em voo, página além da última (com
 *   total > 0) vai para a última com replace (`normalizePage`, D21): link antigo, `?page=99999`
 *   ou exclusão do único estudo da última página.
 * - Rolagem: trocar a página ou o por página leva ao topo o `.new-home-outlet-container` que
 *   contém a tela (`scrollAnchorRef`).
 *
 * @param filtersState - `filters` e `normalizePage` da instância única do `useMyStudiesFilters`
 *   (montada pela casca `MyStudies`; nunca crie outra aqui).
 * @param options - `skip` enquanto não há usuário válido e a âncora da rolagem.
 * @returns Lista, a consulta (`listArgs`), estado, erro e as ações de refetch para o "Tentar de
 *   novo" e a #2004.
 */
export default function useMyStudiesPage(
  { filters, normalizePage }: MyStudiesPageFilters,
  { skip = false, scrollAnchorRef }: UseMyStudiesPageOptions = {},
): UseMyStudiesPageResult {
  const { api } = useMyStudiesProduct();
  const listArgs = toQuery(filters, new Date());
  const {
    data, currentData, error, isLoading, isFetching, fulfilledTimeStamp, refetch,
  } = api.useListQuery(listArgs, { skip });

  // Chave dos argumentos atuais: o refetch manual pendente vale só para eles
  const argsKey = serializeFilters(filters);
  const [refreshKey, setRefreshKey] = useState<string | null>(null);
  const lastRefresh = useRef(0);
  const manualRefreshPending = refreshKey !== null && refreshKey === argsKey;

  const refetchPage = useCallback(() => {
    // Sem consulta iniciada (skip), o `refetch` do RTK lança
    if (skip) return;
    // Antes de ligar a flag: se o `refetch` lançasse, ela ficaria ligada para sempre
    const request = refetch();
    lastRefresh.current += 1;
    const refresh = lastRefresh.current;
    setRefreshKey(argsKey);
    // Só o último pedido desliga a flag: dois cliques seguidos não a desligam cedo
    request.finally(() => {
      if (lastRefresh.current === refresh) setRefreshKey(null);
    });
  }, [refetch, skip, argsKey]);

  const state = useMyStudiesListState({
    isLoading,
    isFetching,
    hasError: error !== undefined,
    hasData: data !== undefined,
    hasCurrentData: currentData !== undefined,
    hasManualRefreshPending: manualRefreshPending,
  });
  const errorKind = useStudiesErrorState(error, { mode: 'primary' });

  // O total vem da resposta dos argumentos ATUAIS: o `data` de outro filtro corrigiria errado
  const currentTotal = currentData?.total;
  useEffect(() => {
    if (isFetching || currentTotal === undefined || currentTotal <= 0) return;
    const lastPage = getPageCount(currentTotal, filters.perPage);
    if (filters.page > lastPage) normalizePage(lastPage);
  }, [isFetching, currentTotal, filters.page, filters.perPage, normalizePage]);

  useScrollToTopOnPageChange(filters, scrollAnchorRef);
  const nowMs = useRowsNow(data);

  return {
    list: data,
    listArgs,
    state,
    hasListOnScreen: data !== undefined && LIST_ON_SCREEN.includes(state),
    isBeyondLastPage: isPageBeyondEnd(data),
    errorKind,
    lastUpdatedAt: fulfilledTimeStamp,
    nowMs,
    refetchPage,
    manualRefreshPending,
  };
}
